import React from 'react';
import {
  Checkbox,
  CheckboxGroup,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Input,
} from 'luna-react'
import { serializePredicates, serializeDefaultValue } from '../utils/FiltersFormatter';

const SliderType = Object.freeze({
  OneDotMin: 0,
  OneDotMax: 1,
  TwoDots: 2,
});

export default function Filter(props) {
  const { type } = props;

  switch (type) {
    case 'radio':
      return createRadioGroup(props);
    case 'checkbox':
      return createCheckboxGroup(props);
    case 'dropdown':
      return createDropdown(props);
    case 'slider':
      return createSlider(props);
    case 'text':
      return createSearchInput(props)
    default:
      return null;
  }
}

function createCheckboxGroup(props) {
  const extendedProps = {
    GroupComponent: CheckboxGroup,
    ItemComponent: Checkbox,
    defaultValue: serializeDefaultValue(props),
    ...props,
  };

  return createGroup(extendedProps)
}

function createRadioGroup(props) {
  const extendedProps = {
    GroupComponent: RadioGroup,
    ItemComponent: Radio,
    defaultValue: serializeDefaultValue(props),
    ...props,
  };

  return createGroup(extendedProps)
}

function generateTooltip(tooltip) {
  if (tooltip !== null) {
    return <span className='table-popover' rel='popover' data-content={`${tooltip}`} />
  }
  return;
}

function createGroup({
  GroupComponent,
  ItemComponent,
  label,
  fieldName,
  trackChange,
  defaultValue,
  options = [],
  tooltip,
}) {

  return (
    <GroupComponent
      name={fieldName}
      label={label}
      onChange={(value) => trackChange(value)}
      defaultValue={defaultValue}
      tooltip={generateTooltip(tooltip)}
    >
      {options.map((option, idx) => {
        const { label, predicates } = option;
        const inputValue = serializePredicates(predicates);

        return (
          <ItemComponent
            name={fieldName}
            value={inputValue}
            label={label}
            key={idx}
            onChange={(value) => trackChange(value)} 
          />
        )
      })}
    </GroupComponent>
  )
}

function createDropdown({
  label,
  fieldName,
  trackChange,
  options = []
}) {
  return (
    <Select name={fieldName} label={label} onChange={(value) => trackChange(value)}>
      {options.map((option, idx) => {
        const { label, predicates } = option;
        const inputValue = serializePredicates(predicates);

        return (
          <option value={inputValue} key={idx}>{label}</option>
        )
      })}
    </Select>
  )
}

function createSearchInput({
  label,
  fieldName,
  columnName,
  trackChange,
  comparator = 'search'
}) {
  function onChange({ target }) {
    const label = valueDecoratorFn(target);
    trackChange(label);
  }

  function valueDecoratorFn({ value }) {
    return `${columnName}|${comparator}|${value}`;
  }

  const decoratedName = `${fieldName}%${columnName}|${comparator}`;

  return (
    <Input
      name={decoratedName}
      label={label}
      onChange={onChange} />
  )
}

function createSlider({
  minValue,
  maxValue,
  step,
  label,
  fieldName,
  columnName,
  sliderType,
  minValueLabelTemplate,
  currentValueLabelTemplate,
  maxValueLabelTemplate,
  stepSize,
  prefix,
  suffix,
  trackChange,
  onApply,
  autoApply,
}) {
  const values = {
    min: parseFloat(minValue),
    max: parseFloat(maxValue),
  };

  const initialValue = getInitialValue({ sliderType, values });
  const sliderRef = React.createRef();

  function getInitialValue({ sliderType, values }) {
    switch (parseInt(sliderType, 10)) {
      case SliderType.OneDotMin:
        return values.min;
      case SliderType.OneDotMax:
        return values.max;
      case SliderType.TwoDots:
        return Object.assign({}, values);
      default:
        return null;
    }
  }

  function valueDecoratorFn({ value, feature }) {
    return `${columnName}|${(feature === 'min') ? 'gte' : 'lte'}|${value}`;
  }

  function onChangeComplete(value) {
    const hasPrimitiveValue = !Object.keys(value).length;
    const label = hasPrimitiveValue
      ? valueDecoratorFn({ feature: 'min', value })
      : `${columnName}|range|${value.min},${value.max}`;

    trackChange(label);

    if (autoApply) {
      onApply({ target: sliderRef.current.formNode });
    }
  }

  return (
    <Slider
      minValue={values.min}
      maxValue={values.max}
      value={initialValue}
      step={parseInt(stepSize, 10)}
      name={fieldName}
      label={label}
      labelPrefix={prefix != null ? prefix : ''}
      labelPostfix={suffix != null ? suffix : ''}
      renderLabel={true}
      valueDecoratorFn={valueDecoratorFn}
      minValueLabelTemplate={minValueLabelTemplate}
      currentValueLabelTemplate={currentValueLabelTemplate}
      maxValueLabelTemplate={maxValueLabelTemplate}
      onChangeComplete={onChangeComplete}
      ref={sliderRef}
    />
  )
}



// WEBPACK FOOTER //
// ./src/components/Filter.js